/* eslint-disable no-unsafe-optional-chaining */
import { useQueryParams } from '@fs/zion-router'
import { useEffect } from 'react'
import { getHighlights, getRecordHighlights, useSlsStateContext } from '@fs/zion-sls'
import { useR2StateContext, useR2DispatchContext } from '../../../providers/r2Provider/R2Context'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

export default function useImageHighlightInitialization() {
  const { currentImageArk } = useSlsStateContext()
  const r2State = useR2StateContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const r2Dispatch = useR2DispatchContext()
  const zionSlsState = useSlsStateContext()
  const imagesData = zionSlsState.images

  const { query } = useQueryParams()

  const thumbnails = sharedBrowserState.thumbnails

  // this useEffect handles saving the highlights on the intial load and any time the data or current ark changes
  // we are saving this locally so that we don't have to parse through all the data every time a highlight is manipulated
  useEffect(() => {
    const duplicateHighlightExists = (array, highlight) => {
      // Dedup highlights from two different images. We already dedup highlights from shared fields in getHighlights, so this does
      // the same thing for shared fields on two different images. See Yahunte surname (fa5ec4e4-8f98-4e02-b20f-e7f01343dc1f) on 3:1:3Q41-KQ4P-D4
      return array.find(
        (h) =>
          highlight.fieldId === h.fieldId &&
          highlight.position.x === h.position.x &&
          highlight.position.y === h.position.y &&
          highlight.position.width === h.position.width &&
          highlight.position.height === h.position.height &&
          highlight?.imageId === h?.imageId
      )
    }

    if (currentImageArk && sharedBrowserState.focusedIndex >= 0) {
      let highlightsYo = getHighlights({ element: imagesData.find((i) => i.partitionKey === currentImageArk) })
      const highlightsNext = getHighlights({
        element: imagesData.find((i) => i.partitionKey === thumbnails[sharedBrowserState.focusedIndex - 1]?.ark),
      })
      const highlightsPrev = getHighlights({
        element: imagesData.find((i) => i.partitionKey === thumbnails[sharedBrowserState.focusedIndex + 1]?.ark),
      })
      highlightsYo = [
        ...highlightsYo,
        ...highlightsNext
          ?.filter((h) => h?.imageId === currentImageArk)
          .filter((h) => !duplicateHighlightExists(highlightsYo, h) && !duplicateHighlightExists(highlightsPrev, h)),
        ...highlightsPrev
          ?.filter((h) => h?.imageId === currentImageArk)
          .filter((h) => !duplicateHighlightExists(highlightsYo, h) && !duplicateHighlightExists(highlightsNext, h)),
      ]
      // A multi-page record only has a full body (with rects for every page) on its "home" image; on its other
      // pages it's a dangling reference with no rects. So, like the field highlights above, we merge in the
      // neighboring images' record rects and keep only the ones tagged for the image we're actually viewing.
      const recordRectsCurrent = getRecordHighlights(imagesData.find((i) => i.partitionKey === currentImageArk))
      const recordRectsNext = getRecordHighlights(
        imagesData.find((i) => i.partitionKey === thumbnails[sharedBrowserState.focusedIndex - 1]?.ark)
      )
      const recordRectsPrev = getRecordHighlights(
        imagesData.find((i) => i.partitionKey === thumbnails[sharedBrowserState.focusedIndex + 1]?.ark)
      )
      const recordRects = [
        ...(recordRectsCurrent || []).filter((h) => h?.imageId === currentImageArk),
        ...(recordRectsNext || []).filter((h) => h?.imageId === currentImageArk),
        ...(recordRectsPrev || []).filter((h) => h?.imageId === currentImageArk),
      ]
      r2Dispatch({ type: 'SET_STATE', value: { recordHighlights: recordRects, recordEditHighlights: recordRects } })
      r2Dispatch({ type: 'SET_HIGHLIGHTS', value: highlightsYo })
      r2Dispatch({ type: 'SET_EDIT_HIGHLIGHTS', value: highlightsYo })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageArk, imagesData, r2Dispatch, sharedBrowserState.focusedIndex, query.view])

  useEffect(() => {
    if (r2State.splitScreen) {
      const referenceImageData = imagesData.find(
        (i) => i.partitionKey === thumbnails?.[r2State.referenceFocusedIndex]?.ark
      )
      const highlightsYo = getHighlights({ element: referenceImageData }).concat(
        // We need highlights that are technically an SLS child of the mainscreen image, but located on the reference image
        getHighlights({
          element: imagesData.find((i) => i.partitionKey === currentImageArk),
        })
      )
      r2Dispatch({ type: 'SET_REFERENCE_HIGHLIGHTS', value: highlightsYo })
      r2Dispatch({ type: 'SET_REFERENCE_EDIT_HIGHLIGHTS', value: highlightsYo })
      // Same dangling-page recovery as the main-screen branch above: a multi-page record only
      // carries rects on its "home" image, so merge the reference page's neighbors and keep the
      // rects tagged for the reference page itself.
      const referenceArk = referenceImageData?.partitionKey
      const recordRects = [
        referenceImageData,
        imagesData.find((i) => i.partitionKey === thumbnails?.[r2State.referenceFocusedIndex - 1]?.ark),
        imagesData.find((i) => i.partitionKey === thumbnails?.[r2State.referenceFocusedIndex + 1]?.ark),
      ].flatMap((image) => (getRecordHighlights(image) || []).filter((h) => h?.imageId === referenceArk))
      r2Dispatch({
        type: 'SET_STATE',
        value: { referenceRecordHighlights: recordRects, referenceRecordEditHighlights: recordRects },
      })
    }
  }, [currentImageArk, imagesData, r2Dispatch, r2State.referenceFocusedIndex, r2State.splitScreen, thumbnails])
}
